import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock } from "vue"

import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import HomePage from "@/components/HomePage.vue";
import LegalPage from "@/components/LegalPage.vue";
import MorePage from "@/components/MorePage.vue";
import MobileHeader from "@/components/MobileHeader.vue";
import MobileHomePage from "@/components/MobileHomePage.vue";
import MobileVecuraPage from "@/components/MobileVecuraPage.vue";
import SiteHeader from "@/components/SiteHeader.vue";
import SiteFooter from "@/components/SiteFooter.vue";
import VecuraPage from "@/components/VecuraPage.vue";
import defaultFavicon from "@/assets/rothdeutsch-favicon.png";
import vecuraFavicon from "@/assets/rothdeutsch-favicon-vecura.png";

type ActiveSection =
  | "profile"
  | "vecura"
  | "expertise"
  | "contact"
  | "more"
  | "legal";
type LegalPageType = "impressum" | "datenschutz";
type ScrollTargetBehavior = "instant" | "smooth";

interface PageMeta {
  title: string;
  description: string;
}

const MOBILE_LAYOUT_QUERY = "(max-width: 760px)";
const DESKTOP_HEADER_SCROLL_OFFSET = 104;
const MOBILE_HEADER_SCROLL_OFFSET = 78;
const SOCIAL_IMAGE_PATH = "/img/icons/android-chrome-512x512.png";

export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const REVEAL_SELECTOR = [
  ".hero__content",
  ".section",
  ".profile-focus__list article",
  ".responsibility__item",
  ".ai-expertise__grid article",
  ".work-modes__list article",
  ".evidence__list article",
  ".contact__channel",
  ".more-page__hero",
  ".more-page__section",
  ".vecura-hero__role",
  ".vecura-pitch__proof article",
  ".vecura-capabilities__grid article",
  ".vecura-case li",
  ".legal-page__section",
  ".mobile-hero",
  ".mobile-section",
  ".mobile-flow article",
  ".mobile-accordion",
  ".mobile-ai__cards article",
  ".mobile-card-list article",
  ".mobile-evidence__timeline article",
  ".mobile-contact-action",
  ".mobile-vecura__hero",
  ".mobile-vecura__section",
  ".mobile-vecura__stats article",
  ".mobile-vecura__capability",
].join(", ");
const currentPath = ref(getCurrentPath());
const activeSection = ref<ActiveSection>("profile");
const isMobileLayout = ref(getMobileLayoutMatches());
let revealObserver: IntersectionObserver | null = null;
let revealRefreshTimer: number | null = null;
let mobileMediaQuery: MediaQueryList | null = null;

const isVecuraRoute = computed(
  () => normalizePath(currentPath.value) === "/vecura"
);
const isMoreRoute = computed(
  () => normalizePath(currentPath.value) === "/mehr"
);
const legalPage = computed<LegalPageType | null>(() => {
  const path = normalizePath(currentPath.value);

  if (path === "/impressum") {
    return "impressum";
  }

  if (path === "/datenschutz") {
    return "datenschutz";
  }

  return null;
});
const isLegalRoute = computed(() => legalPage.value !== null);

function normalizePath(path: string): string {
  const pathOnly = path.split("#")[0];
  const normalized = pathOnly.replace(/\/+$/, "");
  return normalized || "/";
}

function syncCurrentPath(): void {
  currentPath.value = getCurrentPath();
  syncActiveSection();
}

function getCurrentPath(): string {
  return `${window.location.pathname || "/"}${window.location.hash || ""}`;
}

function getMobileLayoutMatches(): boolean {
  if (typeof window === "undefined" || !("matchMedia" in window)) {
    return false;
  }

  return window.matchMedia(MOBILE_LAYOUT_QUERY).matches;
}

function getHeaderScrollOffset(): number {
  return isMobileLayout.value
    ? MOBILE_HEADER_SCROLL_OFFSET
    : DESKTOP_HEADER_SCROLL_OFFSET;
}

function getActiveSectionTriggerOffset(): number {
  return getHeaderScrollOffset() + (isMobileLayout.value ? 98 : 180);
}

function scrollInstantlyTo(top: number): void {
  const root = document.documentElement;
  const previousScrollBehavior = root.style.scrollBehavior;

  // Route changes should not inherit the global smooth-scroll behavior.
  root.style.scrollBehavior = "auto";
  window.scrollTo({ top, left: 0, behavior: "auto" });
  root.style.scrollBehavior = previousScrollBehavior;
}

function scrollToPosition(top: number, behavior: ScrollTargetBehavior): void {
  if (behavior === "instant") {
    scrollInstantlyTo(top);
    return;
  }

  window.scrollTo({ top, left: 0, behavior: "smooth" });
}

function scrollToTarget(
  hash: string,
  behavior: ScrollTargetBehavior = "smooth"
): void {
  if (!hash) {
    scrollToPosition(0, behavior);
    syncActiveSection();
    return;
  }

  const target = document.querySelector<HTMLElement>(hash);

  if (target) {
    const targetTop = target.getBoundingClientRect().top + window.scrollY;

    scrollToPosition(
      Math.max(targetTop - getHeaderScrollOffset(), 0),
      behavior
    );
  }
}

function scheduleRouteScroll(hash: string): void {
  requestAnimationFrame(() => {
    if (hash) {
      scrollToTarget(hash, "smooth");
      window.setTimeout(() => scrollToTarget(hash, "instant"), 360);
      window.setTimeout(syncActiveSection, 440);
      scheduleRevealMotion(380);
      return;
    }

    scrollToTarget("", "instant");
    window.setTimeout(syncActiveSection, 120);
    scheduleRevealMotion(120);
  });
}

function scheduleRevealMotion(delay = 0): void {
  if (revealRefreshTimer !== null) {
    window.clearTimeout(revealRefreshTimer);
  }

  revealRefreshTimer = window.setTimeout(() => {
    revealRefreshTimer = null;
    syncRevealMotion();
  }, delay);
}

function syncRevealMotion(): void {
  const reduceMotion =
    "matchMedia" in window &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const elements = Array.from(
    document.querySelectorAll<HTMLElement>(REVEAL_SELECTOR)
  );

  elements.forEach((element, index) => {
    if (!element.classList.contains("motion-reveal")) {
      element.classList.add("motion-reveal");
      element.style.setProperty("--motion-order", String(index % 5));
    }

    if (reduceMotion || !revealObserver) {
      element.classList.add("motion-reveal--visible");
      return;
    }

    revealObserver.observe(element);
  });
}

function syncActiveSection(): void {
  if (isLegalRoute.value) {
    activeSection.value = "legal";
    return;
  }

  if (isVecuraRoute.value) {
    activeSection.value = "vecura";
    return;
  }

  if (isMoreRoute.value) {
    activeSection.value = "more";
    return;
  }

  const pageBottom =
    document.documentElement.scrollHeight - window.innerHeight - 8;

  if (isMobileLayout.value && window.scrollY >= pageBottom) {
    activeSection.value = "contact";
    return;
  }

  const trackedSections: Array<{
    id: string;
    section: Exclude<ActiveSection, "vecura">;
  }> = [
    {
      id: "expertise",
      section: "expertise",
    },
    {
      id: "contact",
      section: "contact",
    },
  ];
  let nextSection: Exclude<ActiveSection, "vecura"> = "profile";

  trackedSections.forEach(({ id, section }) => {
    const element = document.getElementById(id);

    if (
      element &&
      element.getBoundingClientRect().top <= getActiveSectionTriggerOffset()
    ) {
      nextSection = section;
    }
  });

  activeSection.value = nextSection;
}

async function navigateWithinSite(href: string): Promise<void> {
  const nextUrl = new URL(href, window.location.origin);
  const nextPath = `${nextUrl.pathname}${nextUrl.hash}`;

  window.history.pushState({}, "", nextPath);
  syncCurrentPath();

  await nextTick();
  scheduleRouteScroll(nextUrl.hash);
}

function handlePopState(): void {
  syncCurrentPath();

  nextTick().then(() => {
    scheduleRouteScroll(window.location.hash);
  });
}

function syncMobileLayout(event?: MediaQueryListEvent): void {
  isMobileLayout.value =
    event?.matches ?? mobileMediaQuery?.matches ?? getMobileLayoutMatches();
}

function bindMobileLayout(): void {
  if (typeof window === "undefined" || !("matchMedia" in window)) {
    return;
  }

  mobileMediaQuery = window.matchMedia(MOBILE_LAYOUT_QUERY);
  syncMobileLayout();

  if ("addEventListener" in mobileMediaQuery) {
    mobileMediaQuery.addEventListener("change", syncMobileLayout);
    return;
  }

  mobileMediaQuery.addListener(syncMobileLayout);
}

function unbindMobileLayout(): void {
  if (!mobileMediaQuery) {
    return;
  }

  if ("removeEventListener" in mobileMediaQuery) {
    mobileMediaQuery.removeEventListener("change", syncMobileLayout);
    return;
  }

  mobileMediaQuery.removeListener(syncMobileLayout);
}

function handleDocumentClick(event: MouseEvent): void {
  if (
    event.defaultPrevented ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey
  ) {
    return;
  }

  const target = event.target as HTMLElement | null;
  const link = target?.closest("a");

  if (!link) {
    return;
  }

  const href = link.getAttribute("href");
  const targetAttribute = link.getAttribute("target");

  if (
    !href ||
    href.startsWith("#") ||
    href.startsWith("mailto:") ||
    targetAttribute === "_blank"
  ) {
    return;
  }

  const nextUrl = new URL(href, window.location.origin);

  if (nextUrl.origin !== window.location.origin) {
    return;
  }

  event.preventDefault();
  navigateWithinSite(href);
}

function syncFavicon(): void {
  const faviconHref = isVecuraRoute.value ? vecuraFavicon : defaultFavicon;
  let favicon = document.querySelector<HTMLLinkElement>("link[rel='icon']");

  if (!favicon) {
    favicon = document.createElement("link");
    favicon.rel = "icon";
    document.head.appendChild(favicon);
  }

  favicon.type = "image/png";
  favicon.href = faviconHref;
}

function syncDocumentTheme(): void {
  const vecuraClass = "document-theme--vecura";

  document.documentElement.classList.toggle(vecuraClass, isVecuraRoute.value);
  document.body.classList.toggle(vecuraClass, isVecuraRoute.value);
}

function getPageMeta(): PageMeta {
  const path = normalizePath(currentPath.value);

  if (path === "/vecura") {
    return {
      title: "Vecura - RAG-Plattform für Fachwissen | Timo Rothdeutsch",
      description:
        "Vecura macht komplexes Unternehmens- und Fachwissen über eine produktionsnahe RAG-Plattform nutzbar: mit Quellenbezug, Retrieval und Integration.",
    };
  }

  if (path === "/impressum") {
    return {
      title: "Impressum | Timo Rothdeutsch",
      description:
        "Anbieterkennzeichnung und Kontaktangaben für die Website von Timo Rothdeutsch.",
    };
  }

  if (path === "/datenschutz") {
    return {
      title: "Datenschutz | Timo Rothdeutsch",
      description:
        "Datenschutzhinweise zur Website von Timo Rothdeutsch und zu Kontaktwegen wie E-Mail, LinkedIn und Vecura.",
    };
  }

  if (path === "/mehr") {
    return {
      title: "Info | Timo Rothdeutsch",
      description:
        "Kurzprofil, persönlicher Kontext sowie Impressum und Datenschutz zur Website von Timo Rothdeutsch.",
    };
  }

  return {
    title: "Timo Rothdeutsch - KI, Architektur und technische Verantwortung",
    description:
      "Timo Rothdeutsch arbeitet an KI- und Wissenssystemen, RAG-Architektur, technischer Verantwortung und Aufbauarbeit als Mitgründer und COO bei Vecura.",
  };
}

function syncDocumentMeta(): void {
  const meta = getPageMeta();
  const pageUrl = new URL(window.location.pathname, window.location.origin);
  const socialImageUrl = new URL(SOCIAL_IMAGE_PATH, window.location.origin);

  document.documentElement.lang = "de";
  document.title = meta.title;
  upsertMeta("name", "description", meta.description);
  upsertMeta("property", "og:title", meta.title);
  upsertMeta("property", "og:description", meta.description);
  upsertMeta("property", "og:type", "website");
  upsertMeta("property", "og:url", pageUrl.toString());
  upsertMeta("property", "og:image", socialImageUrl.toString());
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", meta.title);
  upsertMeta("name", "twitter:description", meta.description);
  upsertMeta("name", "twitter:image", socialImageUrl.toString());
  upsertMeta(
    "name",
    "theme-color",
    isVecuraRoute.value ? "#0e151c" : "#f4f1ea"
  );
  upsertCanonical(pageUrl.toString());
}

function upsertMeta(
  attribute: "name" | "property",
  key: string,
  content: string
): void {
  let element = document.head.querySelector<HTMLMetaElement>(
    `meta[${attribute}="${key}"]`
  );

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.content = content;
}

function upsertCanonical(href: string): void {
  let element = document.head.querySelector<HTMLLinkElement>(
    "link[rel='canonical']"
  );

  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.appendChild(element);
  }

  element.href = href;
}

onMounted(() => {
  bindMobileLayout();

  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }

  if ("IntersectionObserver" in window) {
    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          entry.target.classList.add("motion-reveal--visible");
          revealObserver?.unobserve(entry.target);
        });
      },
      {
        rootMargin: "0px 0px -12% 0px",
        threshold: 0.12,
      }
    );
  }

  window.addEventListener("popstate", handlePopState);
  window.addEventListener("hashchange", handlePopState);
  window.addEventListener("scroll", syncActiveSection, { passive: true });
  window.addEventListener("resize", syncActiveSection);
  document.addEventListener("click", handleDocumentClick);
  syncFavicon();
  syncDocumentTheme();
  syncDocumentMeta();

  nextTick().then(() => {
    if (window.location.hash) {
      scrollToTarget(window.location.hash);
    }

    syncActiveSection();
    scheduleRevealMotion(60);
  });
});

watch(isVecuraRoute, () => {
  syncFavicon();
  syncDocumentTheme();
  scheduleRevealMotion(380);
});

watch(isMobileLayout, () => {
  nextTick().then(() => {
    syncActiveSection();
    scheduleRevealMotion(120);
  });
});

watch(currentPath, syncDocumentMeta);

onBeforeUnmount(() => {
  revealObserver?.disconnect();
  document.documentElement.classList.remove("document-theme--vecura");
  document.body.classList.remove("document-theme--vecura");

  if (revealRefreshTimer !== null) {
    window.clearTimeout(revealRefreshTimer);
  }

  window.removeEventListener("popstate", handlePopState);
  window.removeEventListener("hashchange", handlePopState);
  window.removeEventListener("scroll", syncActiveSection);
  window.removeEventListener("resize", syncActiveSection);
  document.removeEventListener("click", handleDocumentClick);
  unbindMobileLayout();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["app-frame", { 'app-frame--vecura': isVecuraRoute.value }])
  }, [
    (isMobileLayout.value)
      ? (_openBlock(), _createBlock(MobileHeader, {
          key: 0,
          "active-section": activeSection.value,
          "current-path": currentPath.value,
          "is-vecura": isVecuraRoute.value
        }, null, 8, ["active-section", "current-path", "is-vecura"]))
      : (_openBlock(), _createBlock(SiteHeader, {
          key: 1,
          "active-section": activeSection.value,
          "current-path": currentPath.value,
          "is-vecura": isVecuraRoute.value
        }, null, 8, ["active-section", "current-path", "is-vecura"])),
    _createElementVNode("main", null, [
      _createVNode(_Transition, {
        name: "page-shift",
        mode: "out-in"
      }, {
        default: _withCtx(() => [
          (isVecuraRoute.value && isMobileLayout.value)
            ? (_openBlock(), _createBlock(MobileVecuraPage, { key: "mobile-vecura" }))
            : (isVecuraRoute.value)
              ? (_openBlock(), _createBlock(VecuraPage, { key: "vecura" }))
              : (isMoreRoute.value)
                ? (_openBlock(), _createBlock(MorePage, { key: "more" }))
                : (isLegalRoute.value)
                  ? (_openBlock(), _createBlock(LegalPage, {
                      key: legalPage.value || 'legal',
                      page: legalPage.value || 'impressum'
                    }, null, 8, ["page"]))
                  : (isMobileLayout.value)
                    ? (_openBlock(), _createBlock(MobileHomePage, { key: "mobile-home" }))
                    : (_openBlock(), _createBlock(HomePage, { key: "home" }))
        ]),
        _: 1
      })
    ]),
    _createVNode(SiteFooter)
  ], 2))
}
}

})